/**
 * הגדרות וחיבורים (spec §4.6 + BRIEF §9.1 "שיוך חשבונות").
 * Six groups: חיבורים · חשבונות וכרטיסים · משתמשים · התראות · מראה · פרטיות וייצוא.
 * Owner-only controls are disabled for a member (api-personal.md §1.2).
 */

import { useRef, useState } from 'react';
import type { FormEvent, MutableRefObject } from 'react';
import type { Account, AlertRules, CardsResponse, Connection, SettingsResponse, SummaryResponse, UsersResponse } from '../../../shared-types.ts';
import { fmtDate, fmtDateTime, fmtMoney, initial } from '../format.ts';
import { ACCOUNT_TYPE, CONNECTION_STATUS, CREDIT_STATUS, he, providerName } from '../i18n/he.ts';
import { Icon } from '../components/Icon.tsx';
import { Chip, Ltr, ProviderLogo, Section, Segmented, Switch } from '../components/ui.tsx';
import { errorMessage } from '../api-select.ts';
import { useApp } from '../hooks/useApp.tsx';
import { useQuery } from '../hooks/useQuery.ts';
import { cacheKey } from '../hooks/cache.ts';
import { Link, useRouter } from '../hooks/useRouter.tsx';
import { buildPath, settingsTab, SETTINGS_TABS, type SettingsTab } from '../router.ts';
import { useIsMobile } from '../hooks/useMediaQuery.ts';

const GROUP_ICON: Record<SettingsTab, string> = {
  connections: 'link',
  accounts: 'card',
  users: 'users',
  alerts: 'bell',
  appearance: 'sun',
  privacy: 'db',
};

export function SettingsScreen({ summary }: { summary: SummaryResponse | undefined }) {
  const { route } = useRouter();
  const isMobile = useIsMobile();
  const tab = settingsTab(route);
  const isSub = Boolean(route.param) && isMobile;

  return (
    <div className="screen">
      <div className="g12">
        <div className={isSub ? 'only-d' : ''} style={{ ['--c' as string]: '3' } as Record<string, string>}>
          <nav className="subnav" aria-label={he.settings.groupsLabel}>
            {SETTINGS_TABS.map((key) => (
              <Link key={key} to={buildPath('settings', key)} aria-current={tab === key ? 'true' : undefined}>
                <Icon name={GROUP_ICON[key]} className="s" />
                <span className="grow">{he.settings.groups[key]}</span>
                <Icon name="chevL" className="xs t3 only-m" />
              </Link>
            ))}
          </nav>
        </div>
        <div className={isSub ? '' : 'only-d'} style={{ ['--c' as string]: '9' } as Record<string, string>}>
          <section className="sec settings-body" style={{ border: 0, paddingTop: '4px' }} aria-labelledby="h-set">
            <h2 id="h-set" className="only-d" style={{ fontSize: '18px' }}>
              {he.settings.groups[tab]}
            </h2>
            {tab === 'connections' ? <ConnectionsTab summary={summary} /> : null}
            {tab === 'accounts' ? <AccountsTab /> : null}
            {tab === 'users' ? <UsersTab /> : null}
            {tab === 'alerts' ? <AlertsTab /> : null}
            {tab === 'appearance' ? <AppearanceTab /> : null}
            {tab === 'privacy' ? <PrivacyTab /> : null}
          </section>
        </div>
      </div>
    </div>
  );
}

function ConnectionsTab({ summary }: { summary: SummaryResponse | undefined }) {
  const { api, user } = useApp();
  const query = useQuery<{ items: Connection[] }>(cacheKey('connections'), () => api.connections(), { scopes: ['connections', 'summary'] });
  const credits = summary?.credits;
  const items = query.data?.items ?? [];
  return (
    <>
      <div className="notice">
        <Icon name="coins" />
        <span>
          {credits && credits.plan && credits.usedThisMonth !== null
            ? he.settings.connSummary(items.length, credits.plan.tier, `${credits.usedThisMonth} מתוך ${credits.plan.credits}`)
            : he.settings.connSummaryNoCredits(items.length)}
        </span>
      </div>
      {items.map((connection) => {
        const status = CONNECTION_STATUS[connection.status];
        const expired = connection.needsRelink;
        return (
          <div className={`conn${expired ? ' expired' : ''}`} key={connection.id}>
            <div className="row between wrap">
              <span className="row" style={{ gap: '10px' }}>
                <ProviderLogo providerId={connection.providerId} size="lg" title={providerName(connection.providerId)} />
                <b>
                  {providerName(connection.providerId)} {connection.maskedPans[0] ? <Ltr>{connection.maskedPans[0]}</Ltr> : null}
                </b>
              </span>
              <Chip tone={status.chip as 'good'} icon={status.icon}>
                {status.label}
              </Chip>
            </div>
            <dl className="kv">
              <dt>{he.settings.includes}</dt>
              <dd>{connection.restrictedTo.join(', ')}</dd>
              <dt>{he.settings.lastFetched}</dt>
              <dd>{connection.lastFetchedAt ? <span className="num">{fmtDateTime(connection.lastFetchedAt)}</span> : '—'}</dd>
              <dt>{expired ? he.settings.consentExpired : he.settings.consentUntil}</dt>
              <dd>
                <span className="num">
                  {fmtDate(connection.expiryDate)}
                  {connection.expiryDate === summary?.asOf ? ` ${he.settings.todaySuffix}` : ''}
                </span>
              </dd>
            </dl>
            {expired ? (
              <>
                <div className="row wrap">
                  <a className="btn primary" href="https://financy.open-finance.ai/" target="_blank" rel="noreferrer noopener">
                    <Icon name="external" className="s" />
                    {he.settings.reconnect}
                  </a>
                </div>
                <p className="meta">{he.settings.reconnectNote}</p>
              </>
            ) : null}
          </div>
        );
      })}
      <p className="meta">{he.settings.addConnection}</p>
      <div>
        <RefreshAllButton summary={summary} disabled={user?.role !== 'owner'} />
      </div>
    </>
  );
}

function RefreshAllButton({ summary, disabled }: { summary: SummaryResponse | undefined; disabled: boolean }) {
  const { openDialog, startSync, toast } = useApp();
  const credits = summary?.credits;
  return (
    <button
      type="button"
      className="btn"
      aria-disabled={disabled || undefined}
      onClick={() => {
        if (disabled) {
          toast(he.top.refreshMember, { error: true });
          return;
        }
        openDialog({
          title: he.refresh.title,
          text:
            credits && credits.plan && credits.usedThisMonth !== null
              ? he.refresh.body(String(credits.plan.credits - credits.usedThisMonth), String(credits.plan.credits))
              : he.refresh.bodyUnknown,
          actions: [
            { label: he.refresh.confirm, tone: 'primary', onClick: () => startSync(true) },
            { label: he.app.cancel },
          ],
        });
      }}
    >
      <Icon name="refresh" className="s" />
      {he.settings.refreshAll}
    </button>
  );
}

function AccountsTab() {
  const { api, user, toast } = useApp();
  const accounts = useQuery<{ items: Account[] }>(cacheKey('accounts'), () => api.accounts(), { scopes: ['accounts'] });
  const cards = useQuery<CardsResponse>(cacheKey('cards'), () => api.cards(), { scopes: ['cards'] });
  const isOwner = user?.role === 'owner';
  const items = accounts.data?.items ?? [];
  const order: Record<string, number> = { CHECKING: 0, SAVINGS: 1, CARD: 2, LOAN: 3, SECURITY: 4 };
  const sorted = [...items].sort(
    (x, y) => (order[x.type] ?? 9) - (order[y.type] ?? 9) || Number(x.creditStatus === 'deleted') - Number(y.creditStatus === 'deleted'),
  );
  const activeCards = (cards.data?.items ?? []).filter((card) => card.creditStatus === 'enabled');

  return (
    <>
      <div className="stack" style={{ gap: '8px' }}>
        <h3 style={{ fontSize: '15px' }}>{he.settings.assignTitle}</h3>
        <p className="small t2">{he.settings.assignBody}</p>
        <div>
          {sorted.map((account) => {
            const name = `${account.name}${account.numberLast4 ? ` ${account.type === 'CARD' ? '****' : '***'}${account.numberLast4}` : ''}`;
            return (
              <div className={`assign-row${account.assigned ? '' : ' off'}`} key={account.id}>
                <ProviderLogo providerId={account.providerId} title={providerName(account.providerId)} />
                <span className="who">
                  <span className="row wrap" style={{ gap: '6px' }}>
                    <Ltr>{name}</Ltr>
                    {account.creditStatus ? (
                      <Chip tone={CREDIT_STATUS[account.creditStatus].chip as 'good'} icon={CREDIT_STATUS[account.creditStatus].icon}>
                        {CREDIT_STATUS[account.creditStatus].label}
                      </Chip>
                    ) : null}
                  </span>
                  <small>
                    {ACCOUNT_TYPE[account.type]}
                    {account.product ? ` · ${account.product}` : ''}
                  </small>
                </span>
                <label className="sw">
                  <span className="only-d">{he.settings.assignSwitch}</span>
                  <Switch
                    checked={account.assigned}
                    disabled={!isOwner}
                    label={he.settings.assignAria(name)}
                    onChange={(next) => {
                      api.updateAccount(account.id, { assigned: next }).then(() => {
                        accounts.refetch();
                        toast(next ? he.settings.assigned(name) : he.settings.unassigned(name));
                      });
                    }}
                  />
                </label>
              </div>
            );
          })}
        </div>
        <p className="meta">{he.settings.assignFoot}</p>
      </div>

      <hr className="divider" />
      <div className="stack" style={{ gap: '8px' }}>
        <h3 style={{ fontSize: '15px' }}>{he.settings.cyclesTitle}</h3>
        <p className="small t2">{he.settings.cyclesBody}</p>
        {activeCards.map((card) => (
          <div className="rule" key={card.account.id}>
            <span className="t">
              <span className="row" style={{ gap: '8px' }}>
                <ProviderLogo providerId={card.account.providerId} title={providerName(card.account.providerId)} />
                <Ltr>{`${card.account.name} ****${card.account.numberLast4 ?? ''}`}</Ltr>
              </span>
              <small>
                {he.settings.cycleSubline(card.settings.dueDaySource === 'financy' ? 'Financy' : card.settings.dueDaySource === 'manual' ? he.app.manual : 'נגזר מהעו"ש')}
              </small>
            </span>
            <label className="small" htmlFor={`cut-${card.account.id}`}>
              {he.settings.cutoff}
            </label>
            <input
              className="input"
              id={`cut-${card.account.id}`}
              type="number"
              inputMode="numeric"
              min={1}
              max={28}
              defaultValue={card.settings.cutoffDay}
              disabled={!isOwner}
              onBlur={(event) => {
                const value = Math.max(1, Math.min(28, Number(event.target.value) || 1));
                api.updateCard(card.account.id, { cutoffDay: value }).then(() => {
                  cards.refetch();
                  toast(he.settings.cutoffSaved(value));
                });
              }}
            />
            <label className="small" htmlFor={`due-${card.account.id}`}>
              {he.settings.due}
            </label>
            <input
              className="input"
              id={`due-${card.account.id}`}
              type="number"
              inputMode="numeric"
              min={1}
              max={28}
              defaultValue={card.settings.dueDay}
              disabled={!isOwner}
              onBlur={(event) => {
                const value = Math.max(1, Math.min(28, Number(event.target.value) || 1));
                api.updateCard(card.account.id, { dueDay: value }).then(() => {
                  cards.refetch();
                  toast(he.settings.dueSaved(value));
                });
              }}
            />
          </div>
        ))}
      </div>

      <hr className="divider" />
      <div className="stack" style={{ gap: '8px' }}>
        <h3 style={{ fontSize: '15px' }}>{he.settings.notesTitle}</h3>
        <p className="small t2">{he.settings.notesBody}</p>
        {sorted
          .filter((account) => account.type === 'SAVINGS' || account.type === 'LOAN')
          .map((account) => (
            <div className="rule" key={`note-${account.id}`}>
              <span className="t">
                <Ltr>{`${account.name}${account.numberLast4 ? ` ***${account.numberLast4}` : ''}`}</Ltr>
                <small>{account.type === 'SAVINGS' ? he.settings.maturityDate : `${he.settings.monthlyPayment} · ${he.settings.remainingPayments}`}</small>
              </span>
              {account.type === 'SAVINGS' ? (
                <input
                  className="input"
                  type="date"
                  style={{ width: '160px' }}
                  aria-label={he.settings.maturityDate}
                  defaultValue={account.note?.maturityDate ?? ''}
                  disabled={!isOwner}
                  onBlur={(event) => {
                    if (!event.target.value) return;
                    api.updateAccount(account.id, { note: { maturityDate: event.target.value } }).then(() => {
                      accounts.refetch();
                      toast(he.settings.noteSaved);
                    });
                  }}
                />
              ) : (
                <>
                  <input
                    className="input"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    aria-label={he.settings.monthlyPayment}
                    defaultValue={account.note?.monthlyPaymentAgorot ? account.note.monthlyPaymentAgorot / 100 : ''}
                    disabled={!isOwner}
                    onBlur={(event) => {
                      const value = Number(event.target.value);
                      if (!(value >= 0)) {
                        toast(he.settings.invalidValue, { error: true });
                        return;
                      }
                      api.updateAccount(account.id, { note: { monthlyPaymentAgorot: Math.round(value * 100) } }).then(() => {
                        accounts.refetch();
                        toast(he.settings.noteSaved);
                      });
                    }}
                  />
                  <input
                    className="input"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={600}
                    aria-label={he.settings.remainingPayments}
                    defaultValue={account.note?.remainingPayments ?? ''}
                    disabled={!isOwner}
                    onBlur={(event) => {
                      const value = Number(event.target.value);
                      if (!(value >= 0)) {
                        toast(he.settings.invalidValue, { error: true });
                        return;
                      }
                      api.updateAccount(account.id, { note: { remainingPayments: Math.round(value) } }).then(() => {
                        accounts.refetch();
                        toast(he.settings.noteSaved);
                      });
                    }}
                  />
                </>
              )}
            </div>
          ))}
      </div>
    </>
  );
}

function UsersTab() {
  const { api, user, openDialog, toast } = useApp();
  const users = useQuery<UsersResponse>(cacheKey('users'), () => api.users(), { scopes: ['users'] });
  const items = users.data?.items ?? [];
  return (
    <>
      <div className="tblscroll">
        <table className="utable">
          <thead>
            <tr>
              <th>{he.settings.usersName}</th>
              <th>{he.settings.usersEmail}</th>
              <th>{he.settings.usersRole}</th>
              <th>{he.settings.usersLastLogin}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id}>
                <td>
                  <span className="row">
                    <span className="avatar">{initial(row.displayName)}</span>
                    {row.displayName}
                  </span>
                </td>
                <td>
                  <Ltr>{row.email}</Ltr>
                </td>
                <td>{row.role === 'owner' ? he.roles.owner : he.roles.memberF}</td>
                <td>
                  <span className="num">{fmtDateTime(row.lastLoginAt)}</span>
                </td>
                <td>
                  {row.id === user?.id ? (
                    <button
                      type="button"
                      className="btn sm"
                      onClick={() =>
                        openDialog({
                          title: he.settings.pwTitle,
                          content: <ChangePasswordForm />,
                          actions: [{ label: he.app.cancel }],
                        })
                      }
                    >
                      {he.settings.changePassword}
                    </button>
                  ) : user?.role === 'owner' ? (
                    <button
                      type="button"
                      className="btn sm danger"
                      onClick={() =>
                        openDialog({
                          title: he.settings.removeTitle,
                          text: he.settings.removeBody(row.displayName),
                          role: 'alertdialog',
                          actions: [
                            { label: he.settings.remove, tone: 'danger', onClick: () => toast(he.settings.removed(row.displayName)) },
                            { label: he.app.cancel },
                          ],
                        })
                      }
                    >
                      {he.settings.remove}
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="meta">{he.settings.addUserNote}</p>
    </>
  );
}

/**
 * `POST /api/auth/password` (ARCHITECTURE §13.2: the UI must offer a password change, because first
 * boot hands out a generated password in `INITIAL-PASSWORDS.txt` and that file is meant to be deleted).
 *
 * The server replaces the session on success — it destroys every session of the user and issues a new
 * cookie for this one — so other devices are signed out and this one stays in. Validation is
 * duplicated here only to keep the round trip out of the obvious mistakes; the server is the authority
 * (`WEAK_PASSWORD`, `INVALID_CREDENTIALS`), and its error text is what the user sees when they differ.
 */
function ChangePasswordForm() {
  const { api, closeLayer, toast } = useApp();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const currentRef = useRef<HTMLInputElement | null>(null);
  const nextRef = useRef<HTMLInputElement | null>(null);
  const repeatRef = useRef<HTMLInputElement | null>(null);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    const current = currentRef.current?.value ?? '';
    const next = nextRef.current?.value ?? '';
    const repeat = repeatRef.current?.value ?? '';
    if (!current || !next || !repeat) {
      setError(he.settings.pwRequired);
      return;
    }
    if (next.length < 10) {
      setError(he.settings.pwShort);
      nextRef.current?.focus();
      return;
    }
    if (next !== repeat) {
      setError(he.settings.pwMismatch);
      repeatRef.current?.focus();
      return;
    }
    if (next === current) {
      setError(he.settings.pwSame);
      nextRef.current?.focus();
      return;
    }
    setError(null);
    setBusy(true);
    api.changePassword({ current, next }).then(
      () => {
        setBusy(false);
        closeLayer();
        toast(he.settings.pwDone);
      },
      (err: unknown) => {
        setBusy(false);
        setError(errorMessage(err));
        currentRef.current?.focus();
      },
    );
  };

  const field = (id: string, label: string, ref: MutableRefObject<HTMLInputElement | null>, autoComplete: string, autoFocus = false) => (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input ref={ref} className="input" id={id} type="password" dir="ltr" autoComplete={autoComplete} autoFocus={autoFocus} onChange={() => setError(null)} />
    </div>
  );

  return (
    <form id="pw-form" noValidate onSubmit={submit} style={{ display: 'grid', gap: '10px', textAlign: 'start' }}>
      {field('pw-current', he.settings.pwCurrent, currentRef, 'current-password', true)}
      {field('pw-next', he.settings.pwNew, nextRef, 'new-password')}
      {field('pw-repeat', he.settings.pwRepeat, repeatRef, 'new-password')}
      <p className="meta">{he.settings.pwHint}</p>
      <div className="field-error" id="pw-err" role="alert" hidden={error === null}>
        <Icon name="alertCircle" className="xs" />
        <span>{error}</span>
      </div>
      <button type="submit" className="btn primary" disabled={busy} aria-describedby="pw-err">
        {busy ? he.settings.pwSaving : he.settings.pwSave}
      </button>
    </form>
  );
}

function AlertsTab() {
  const { api, user, toast } = useApp();
  const settings = useQuery<SettingsResponse>(cacheKey('settings'), () => api.settings(), { scopes: ['settings'] });
  const [draft, setDraft] = useState<Partial<AlertRules>>({});
  const data = settings.data;
  if (!data) return <div className="sk" style={{ height: '260px' }} />;
  const rules = { ...data.alerts, ...draft };
  const isOwner = user?.role === 'owner';

  const numberRule = (key: keyof AlertRules, label: string, sub: string, value: number, suffix: string, extra?: { min?: number; max?: number; step?: number }) => (
    <div className="rule" key={key}>
      <button type="button" className="switch" role="switch" aria-checked="true" aria-label={label} disabled />
      <span className="t">
        {label}
        <small>{sub}</small>
      </span>
      <input
        className="input"
        type="number"
        inputMode="numeric"
        min={extra?.min}
        max={extra?.max}
        step={extra?.step}
        value={value}
        disabled={!isOwner}
        aria-label={label}
        onChange={(event) => setDraft((prev) => ({ ...prev, [key]: Number(event.target.value) }))}
      />
      {suffix}
    </div>
  );

  return (
    <>
      <div>
        {numberRule(
          'cardDueDays',
          he.settings.ruleDue,
          `${he.settings.ruleDueSub} · ${he.settings.ruleDefault(data.alertDefaults.cardDueDays)}`,
          rules.cardDueDays,
          he.settings.days,
          { min: 1, max: 14 },
        )}
        {numberRule('thresholdPct', he.settings.ruleThr, he.settings.ruleThrSub, rules.thresholdPct, '%', { min: 0, max: 100, step: 5 })}
        <div className="rule">
          <button type="button" className="switch" role="switch" aria-checked="true" aria-label={he.settings.ruleLarge} disabled />
          <span className="t">
            {he.settings.ruleLarge}
            <small>{he.settings.ruleLargeSub}</small>
          </span>
          ₪
          <input
            className="input"
            type="number"
            inputMode="numeric"
            min={100}
            step={100}
            value={Math.round(rules.largeTxAgorot / 100)}
            disabled={!isOwner}
            aria-label={he.settings.ruleLarge}
            onChange={(event) => setDraft((prev) => ({ ...prev, largeTxAgorot: Math.round(Number(event.target.value) * 100) }))}
          />
        </div>
        <div className="rule">
          <button type="button" className="switch" role="switch" aria-checked="true" aria-label={he.settings.ruleReturns} disabled />
          <span className="t">
            {he.settings.ruleReturns}
            <small>{he.settings.ruleReturnsSub}</small>
          </span>
        </div>
        {numberRule('staleHours', he.settings.ruleStale, he.settings.ruleStaleSub, rules.staleHours, he.settings.hours, { min: 6, max: 168 })}
        <div className="rule">
          <button type="button" className="switch" role="switch" aria-checked="true" aria-label={he.settings.ruleConn} disabled />
          <span className="t">
            {he.settings.ruleConn}
            <small>{he.settings.ruleConnSub}</small>
          </span>
        </div>
      </div>
      <div>
        <button
          type="button"
          className="btn primary"
          aria-disabled={!isOwner || undefined}
          onClick={() => {
            if (!isOwner) {
              toast(he.settings.ownerOnly, { error: true });
              return;
            }
            api.updateSettings({ alerts: draft }).then(() => {
              setDraft({});
              settings.refetch();
              toast(he.settings.rulesSaved);
            });
          }}
        >
          {he.app.save}
        </button>
      </div>
    </>
  );
}

function AppearanceTab() {
  const { appearance, setAppearance, api } = useApp();
  return (
    <>
      <div className="rule">
        <span className="t">
          {he.settings.theme}
          <small>{he.settings.themeSub}</small>
        </span>
        <Segmented
          value={appearance.theme}
          label={he.settings.theme}
          options={[
            ['light', he.settings.themeLight],
            ['dark', he.settings.themeDark],
            ['auto', he.settings.themeAuto],
          ]}
          onChange={(theme) => {
            setAppearance({ theme });
            void api.updateSettings({ appearance: { theme } }).catch(() => undefined);
          }}
        />
      </div>
      <div className="rule">
        <span className="t">{he.settings.textSize}</span>
        <Segmented
          value={appearance.textSize}
          label={he.settings.textSize}
          options={[
            ['normal', he.settings.textNormal],
            ['large', he.settings.textLarge],
          ]}
          onChange={(textSize) => {
            setAppearance({ textSize });
            void api.updateSettings({ appearance: { textSize } }).catch(() => undefined);
          }}
        />
      </div>
      <div className="rule">
        <Switch
          checked={appearance.exactNumbers}
          label={he.settings.exactNumbers}
          onChange={(exactNumbers) => {
            setAppearance({ exactNumbers });
            void api.updateSettings({ appearance: { exactNumbers } }).catch(() => undefined);
          }}
        />
        <span className="t">
          {he.settings.exactNumbers}
          <small>{appearance.exactNumbers ? he.settings.on : he.settings.off}</small>
        </span>
      </div>
    </>
  );
}

function PrivacyTab() {
  const { api, period, toast } = useApp();
  const [exportPeriod, setExportPeriod] = useState<string>(String(period));
  return (
    <>
      <div className="rule">
        <span className="t">
          {he.settings.exportTitle}
          <small>{he.settings.exportSub}</small>
        </span>
        <div className="selwrap">
          <select className="select" aria-label={he.settings.exportPeriodAria} value={exportPeriod} onChange={(event) => setExportPeriod(event.target.value)}>
            <option value="month">{he.cats.thisMonth}</option>
            <option value="prev">{he.settings.exportPrevMonth}</option>
            <option value="3m">{he.period.m3}</option>
            <option value="12m">{he.period.m12}</option>
          </select>
          <Icon name="chevD" className="xs" />
        </div>
        <a
          className="btn"
          href={api.exportUrl({ period: exportPeriod })}
          onClick={(event) => {
            if (api.isMock) {
              event.preventDefault();
              toast(he.settings.exportStarted);
            }
          }}
        >
          <Icon name="download" className="s" />
          {he.settings.export}
        </a>
      </div>
      <p className="meta">{he.settings.privacyNote}</p>
    </>
  );
}
